import React, { useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { Transition } from 'react-transition-group'
import NavLink from './NavLink'
import SocialLinks from './SocialLinks'

const NAV_ITEMS = [
  { to: '/', label: 'Home', number: '01' },
  { to: '/blog', label: 'Blog', number: '02' },
  { to: '/talks', label: 'Talks', number: '03' },
  { to: '/links', label: 'Links', number: '04' },
  { to: '/about', label: 'About', number: '05' },
]

const STAGGER_DELAY = 80
const ENTER_DURATION = 400
const EXIT_DURATION = 300

const DialogContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgb(var(--bg-color));
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6rem 4.5rem;
  overflow-y: auto;
  transition: opacity ${EXIT_DURATION}ms ease;

  @media screen and (min-width: 880px) {
    padding: 12rem;
  }
`

const NavList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`

const Footer = styled.div`
  margin-top: auto;
  padding-top: 2rem;
`

const overlayTransitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
}

const linkTransitionStyles = (index, state) => {
  const isEntering = state === 'entering' || state === 'entered'

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (reducedMotion) {
    return { opacity: isEntering ? 1 : 0 }
  }

  return isEntering
    ? {
        opacity: 1,
        transform: 'translateY(0)',
        transition: `opacity ${ENTER_DURATION}ms ease ${index * STAGGER_DELAY}ms, transform ${ENTER_DURATION}ms ease ${index * STAGGER_DELAY}ms`,
      }
    : {
        opacity: 0,
        transform: 'translateY(20px)',
      }
}

const Overlay = ({ isOpen, onClose, buttonRef }) => {
  const overlayRef = useRef(null)
  const nodeRef = useRef(null)

  const handleFocusTrap = useCallback(
    (e) => {
      if (e.key !== 'Tab' || !overlayRef.current) return

      const focusableInOverlay = Array.from(
        overlayRef.current.querySelectorAll(
          'a[href], button, [tabindex]:not([tabindex="-1"])'
        )
      )
      const focusable = [buttonRef?.current, ...focusableInOverlay].filter(
        Boolean
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    [buttonRef]
  )

  useEffect(() => {
    if (!isOpen) return

    document.addEventListener('keydown', handleFocusTrap)

    const button = buttonRef?.current
    return () => {
      document.removeEventListener('keydown', handleFocusTrap)
      button?.focus()
    }
  }, [isOpen, handleFocusTrap, buttonRef])

  const handleEntered = useCallback(() => {
    overlayRef.current?.querySelector('a[href]')?.focus()
  }, [])

  return (
    <Transition
      in={isOpen}
      timeout={{ enter: ENTER_DURATION, exit: EXIT_DURATION }}
      unmountOnExit
      nodeRef={nodeRef}
      onEntered={handleEntered}
    >
      {(state) => (
        <DialogContainer
          ref={(el) => {
            nodeRef.current = el
            overlayRef.current = el
          }}
          id="nav-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={overlayTransitionStyles[state]}
        >
          <nav aria-label="Site navigation">
            <NavList>
              {NAV_ITEMS.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  number={item.number}
                  onClick={onClose}
                  style={linkTransitionStyles(index, state)}
                >
                  {item.label}
                </NavLink>
              ))}
            </NavList>
          </nav>
          <Footer>
            <SocialLinks />
          </Footer>
        </DialogContainer>
      )}
    </Transition>
  )
}

export default Overlay
